// 고객사 기준 정보 — «이력 정리 · <업체명>» 카드(수기 이력: 일자·유형·배상금액·내용 등록 · 유형 필터·내용 검색 · 삭제).
// 입력칸은 상세 응답의 can_center_edit 또는 can_edit 일 때만 · 삭제 단추는 행의 can_delete 일 때만(서버가 정본 · 이미 403).
// 머리 오른쪽 «N건 · 배상금액 합계» 는 그 고객사 전체(거름과 무관 · 서버 count·amount_sum).
import { useEffect, useState } from 'react';
import { workCustomersAPI } from '../../api/client';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { Button, SearchInput } from '../../components/ui';
import { kstToday } from '../../utils/kstTime';
import { COLORS, badge, card, input } from '../../utils/uiStyles';
import { DASH, LOG_TYPES, amountInput, dash, errorText, fmtWon, parseAmount } from './workCustomerLabels';
import { SEARCH_DEBOUNCE_MS } from './HistoryCard';

export const LOG_COLUMNS = ['일자', '유형', '내용', '배상금액', '작성자', ''];

const th = { textAlign: 'left', padding: '7px 8px', fontSize: '12px', fontWeight: 500, color: COLORS.textSub, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top', wordBreak: 'break-word' };
const small = { ...input, width: 'auto', padding: '5px 8px', fontSize: '12px' };
const TYPE_TONE = { 오출고: 'red', 오배송: 'orange', 파손: 'pink', 재고차이: 'purple', 출고지연: 'yellow', 협의: 'blue', 기타: 'gray' };

const emptyForm = (types) => ({ log_date: kstToday(), log_type: types[0] || '', amount: '', content: '' });

export default function LogsCard({ customerId, customerName, canWrite, logTypes, reloadKey }) {
  const types = Array.isArray(logTypes) && logTypes.length ? logTypes : LOG_TYPES;
  const [form, setForm] = useState(() => emptyForm(types));
  const [typeFilter, setTypeFilter] = useState('');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setForm(emptyForm(types));
    setTypeFilter('');
    setQuery('');
    setDebounced('');
  }, [customerId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (customerId == null) { setData(null); return undefined; }
    let alive = true;
    const params = {};
    if (typeFilter) params.type = typeFilter;
    if (debounced) params.q = debounced;
    setError('');
    workCustomersAPI.logs(customerId, params)
      .then((res) => { if (alive) setData(res && res.data ? res.data : { items: [], count: 0, amount_sum: 0 }); })
      .catch((e) => { if (alive) { setData(null); setError(errorText(e, '수기 이력을 불러오지 못했습니다')); } });
    return () => { alive = false; };
  }, [customerId, typeFilter, debounced, reloadKey, tick]);

  const items = data && Array.isArray(data.items) ? data.items : [];
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: key === 'amount' ? amountInput(e.target.value) : e.target.value }));
  const content = form.content.trim();

  const submit = async () => {
    if (!content || busy) return;
    setBusy(true);
    try {
      await workCustomersAPI.createLog(customerId, {
        log_date: form.log_date, log_type: form.log_type, content, amount: parseAmount(form.amount),
      });
      toast('등록했습니다');
      setForm((f) => ({ ...f, amount: '', content: '' }));
      setTick((n) => n + 1);
    } catch (e) {
      alertDialog(errorText(e, '수기 이력을 등록하지 못했습니다'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!(await confirmDialog('이 수기 이력을 삭제할까요?', { danger: true, okText: '삭제' }))) return;
    try {
      await workCustomersAPI.deleteLog(customerId, row.id);
      toast('삭제했습니다');
      setTick((n) => n + 1);
    } catch (e) {
      alertDialog(errorText(e, '수기 이력을 삭제하지 못했습니다'));
    }
  };

  return (
    <section data-testid="work-customer-logs" aria-label="이력 정리" style={{ ...card, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
          이력 정리{customerName ? ` · ${customerName}` : ''}
        </h2>
        {data && (
          <span data-testid="work-customer-logs-summary" style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textSub }}>
            {`${data.count || 0}건 · 배상금액 합계 ${fmtWon(data.amount_sum || 0)}`}
          </span>
        )}
      </div>
      {customerId == null ? (
        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>고객사를 고르면 수기 이력이 보입니다.</p>
      ) : (
        <>
          {canWrite && (
            <div data-testid="work-customer-log-form" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
              <input type="date" aria-label="일자" value={form.log_date} onChange={set('log_date')} style={small} />
              <select aria-label="유형" value={form.log_type} onChange={set('log_type')} style={small}>
                {types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input aria-label="배상금액(원)" inputMode="numeric" value={form.amount} onChange={set('amount')}
                placeholder="배상금액(원) · 없으면 비워두기" style={{ ...small, width: '190px' }} />
              <input aria-label="내용" value={form.content} onChange={set('content')}
                placeholder="내용 (예: 10/1 오출고 200건 발생하여 일괄 배상)" style={{ ...small, flex: '1 1 260px' }} />
              <Button onClick={submit} disabled={!content || busy}>{busy ? '등록 중…' : '등록'}</Button>
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <select aria-label="유형 필터" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ ...small, minWidth: '120px' }}>
              <option value="">전체 유형</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="내용 검색" width="220px" />
          </div>
          {error && <div role="alert" style={{ fontSize: '12px', color: COLORS.dangerText, marginBottom: '8px' }}>{error}</div>}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="work-customer-logs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{LOG_COLUMNS.map((h, i) => <th key={h || `c${i}`} scope="col" style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {!error && data && items.length === 0 && (
                  <tr><td colSpan={LOG_COLUMNS.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>수기 이력이 없습니다.</td></tr>
                )}
                {items.map((row) => (
                  <tr key={row.id} data-testid="work-customer-log-row">
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dash(row.log_date)}</td>
                    <td style={td}><span style={badge(TYPE_TONE[row.log_type] || 'gray')}>{row.log_type}</span></td>
                    <td style={td}>{dash(row.content)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.amount == null ? DASH : fmtWon(row.amount)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dash(row.writer)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {row.can_delete && <Button variant="quiet" size="sm" onClick={() => remove(row)}>삭제</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
